/**
 * Cloud-locality derivation helpers — the single place that answers
 * "which cloud (and where in it) does this resource live?" from data the
 * API already returns. Rendered everywhere by <LocalityBadge>.
 *
 * Catalogs carry an expanded `cloudRegion` (migration 083) — derive from it
 * first, with the storage-URI scheme as the cloud-only fallback for legacy
 * regionless rows.
 */

/** Canonical cloud key used for badge tinting. */
export type CloudKey = 'aws' | 'gcp' | 'azure';

export interface Locality {
  cloud: CloudKey;
  /** Short, prominent display name for the cloud (AWS / GCP / Azure). */
  cloudLabel: string;
  /** Optional narrowing detail — a region name or a GCP project id. */
  detail?: string;
}

export const CLOUD_LABELS: Record<CloudKey, string> = {
  aws: 'AWS',
  gcp: 'GCP',
  azure: 'Azure',
};

/** Full customer-facing cloud names (wizard tiles spell these out; badges stay short). */
export const CLOUD_FULL_NAMES: Record<CloudKey, string> = {
  aws: 'Amazon Web Services',
  gcp: 'Google Cloud',
  azure: 'Microsoft Azure',
};

/** URI scheme → cloud. Covers the storage prefixes the API emits/accepts. */
const SCHEME_TO_CLOUD: Record<string, CloudKey> = {
  s3: 'aws',
  s3a: 'aws',
  gs: 'gcp',
  gcs: 'gcp',
  az: 'azure',
  abfs: 'azure',
  abfss: 'azure',
};

/**
 * Derive the cloud a storage URI lives in from its scheme
 * (s3://… → AWS, gs://… → GCP, abfss://… → Azure).
 * Returns undefined for unknown/absent schemes (e.g. local paths).
 */
export const deriveCloudFromUri = (fileUri?: string | null): Locality | undefined => {
  if (!fileUri) return undefined;
  const match = /^([a-z0-9+]+):\/\//i.exec(fileUri.trim());
  if (!match) return undefined;
  const cloud = SCHEME_TO_CLOUD[match[1].toLowerCase()];
  if (!cloud) return undefined;
  return { cloud, cloudLabel: CLOUD_LABELS[cloud] };
};

/**
 * Derive a Secret's locality from its (legacy string) secret type name.
 * Cloud-object-store types map to their cloud; Cloudflare R2 is deliberately
 * egress-neutral (usable from any cloud — see the R2 storage posture), and
 * connection-style secrets (postgresql/adbc) are cloud-agnostic.
 */
export const deriveSecretLocality = (secretTypeName?: string | null): Locality | undefined => {
  const cloud = ({
    'aws-s3': 'aws', s3: 'aws',
    gcs: 'gcp',
    'azure-storage': 'azure', azure: 'azure',
  } as Record<string, CloudKey>)[(secretTypeName ?? '').toLowerCase()];
  if (!cloud) return undefined;
  return { cloud, cloudLabel: CLOUD_LABELS[cloud] };
};

/**
 * Derive an External Secret Provider's locality from its provider type +
 * provider_config. providerConfig is an OPAQUE map (keys pass through the
 * case transform verbatim), so keys here are the API's snake_case ones —
 * camelCase fallbacks are read defensively for mock/seed data.
 * hashicorp_vault (self-hosted, cloud-agnostic) returns undefined.
 */
export const deriveEspLocality = (esp: {
  providerType?: string;
  providerConfig?: Record<string, unknown>;
}): Locality | undefined => {
  const cfg = esp.providerConfig ?? {};
  const str = (v: unknown): string | undefined =>
    typeof v === 'string' && v.trim() ? v.trim() : undefined;

  switch (esp.providerType) {
    case 'aws_secrets_manager':
      return { cloud: 'aws', cloudLabel: CLOUD_LABELS.aws, detail: str(cfg.region) };
    case 'gcp_secret_manager':
      return {
        cloud: 'gcp',
        cloudLabel: CLOUD_LABELS.gcp,
        detail: str(cfg.project_id) ?? str(cfg.projectId),
      };
    case 'azure_keyvault':
      return { cloud: 'azure', cloudLabel: CLOUD_LABELS.azure };
    default:
      return undefined;
  }
};

/**
 * Match a Cloud row's name/label (from the API's clouds table, e.g.
 * name "AWS" / label "aws", name "Azure" / label "azure") to a CloudKey.
 */
export const cloudKeyFromName = (name?: string | null): CloudKey | undefined => {
  if (!name) return undefined;
  const n = name.toLowerCase();
  if (n.includes('aws') || n.includes('amazon')) return 'aws';
  if (n.includes('gcp') || n.includes('google')) return 'gcp';
  if (n.includes('azure')) return 'azure';
  return undefined;
};

/**
 * Locality from an expanded CloudRegion (region + nested cloud), as returned
 * on clusters (`cluster.cloudRegion`) or resolved via CloudContext.
 */
export const deriveCloudRegionLocality = (cloudRegion?: {
  name?: string;
  label?: string;
  cloud?: { name?: string; label?: string };
} | null): Locality | undefined => {
  if (!cloudRegion) return undefined;
  const cloudName = cloudRegion.cloud?.name || cloudRegion.cloud?.label;
  const cloud = cloudKeyFromName(cloudName) ?? cloudKeyFromName(cloudRegion.name || cloudRegion.label);
  if (!cloud) return undefined;
  // Region names/labels often lead with the cloud ("AWS - US West 2",
  // "aws-us-west-2") — strip it so the badge doesn't read "AWS · AWS - …".
  const rawDetail = cloudRegion.name || cloudRegion.label;
  const detail = rawDetail?.replace(
    new RegExp(`^\\s*(?:${cloud}|${CLOUD_LABELS[cloud]})\\s*[-–—·:_]?\\s*`, 'i'),
    '',
  ) || rawDetail;
  return {
    cloud,
    cloudLabel: CLOUD_LABELS[cloud],
    detail,
  };
};


/**
 * A workload identity's cloud is written in its annotation keys:
 * eks.amazonaws.com/role-arn → AWS IRSA; iam.gke.io/gcp-service-account →
 * GKE Workload Identity; azure.workload.identity/client-id → Azure. No
 * recognizable key → neutral (visible in every cloud view).
 */
export const deriveWorkloadIdentityLocality = (wi: {
  annotations?: Record<string, string> | null;
}): Locality | undefined => {
  const keys = Object.keys(wi.annotations ?? {});
  const has = (fragment: string) => keys.some((k) => k.toLowerCase().includes(fragment));
  if (has('eks.amazonaws.com')) return { cloud: 'aws', cloudLabel: CLOUD_LABELS.aws };
  if (has('iam.gke.io')) return { cloud: 'gcp', cloudLabel: CLOUD_LABELS.gcp };
  if (has('azure.workload.identity')) return { cloud: 'azure', cloudLabel: CLOUD_LABELS.azure };
  return undefined;
};

/**
 * WHERE a catalog's data lives, for badges and cloud-filtering. The stored
 * cloud_region wins (for master-provisioned catalogs it is also where the
 * metadata Postgres lives); the storage-URI scheme is the cloud-only
 * fallback. Postgres-only catalogs without a region and Cloudflare R2 are
 * cloud-NEUTRAL (undefined → shown in every cloud view).
 */
export const deriveCatalogLocality = (catalog: {
  catalogType?: 'ducklake' | 'postgres' | 'hive_metastore' | 'iceberg_rest';
  storageType?: string;
  cloudRegion?: { name?: string; label?: string; cloud?: { name?: string; label?: string } } | null;
  fileUri?: string | null;
  dataPath?: string | null;
}): Locality | undefined => {
  if (((catalog.catalogType ?? 'ducklake') === 'postgres' || (catalog.catalogType === 'hive_metastore' && !catalog.fileUri))
      && !catalog.cloudRegion) return undefined;
  if (catalog.catalogType === 'iceberg_rest' && !catalog.cloudRegion) return undefined;   // a REST endpoint names no cloud
  if (catalog.storageType === 'cloudflare-r2') return undefined;
  return deriveCloudRegionLocality(catalog.cloudRegion)
    ?? deriveCloudFromUri(catalog.fileUri ?? catalog.dataPath);
};


/**
 * Keep only the catalogs a cluster in `target` may mount under the organization's
 * cross-cloud / cross-region governance (both flags default ON): same cloud, and same region
 * when cross-region mounts are disabled. Catalogs whose locality can't be determined stay
 * (fail open — the API is the source of truth and refuses a violating pick with a 422).
 */
export const filterCatalogsForTarget = <T extends {
  cloudRegionId?: string; cloudRegion?: { name?: string; label?: string; cloud?: { name?: string; label?: string } } | null;
  fileUri?: string | null; catalogType?: 'ducklake' | 'postgres' | 'hive_metastore' | 'iceberg_rest'; storageType?: string;
  /** GizmoData-managed storage never crosses clouds/regions, whatever the org's flags say. */
  dataSystemManaged?: boolean;
  /** Public GizmoData catalogs (TPC-H) sit in network-fenced buckets: same cloud & region only. */
  isPublic?: boolean;
}>(
  catalogs: T[],
  target: { cloudKey?: CloudKey; regionId?: string },
  org: { disableCrossCloudCatalogMounts?: boolean; disableCrossRegionCatalogMounts?: boolean } | null | undefined,
  cloudKeyForRegionId: (regionId?: string) => CloudKey | undefined,
): T[] => {
  if (!target.cloudKey) return catalogs;
  const orgBlocksCloud = org?.disableCrossCloudCatalogMounts !== false;
  const orgBlocksRegion = org?.disableCrossRegionCatalogMounts !== false;
  return catalogs.filter((c) => {
    // Managed storage (egress billed to GizmoData) and public catalogs (fenced buckets):
    // always same cloud & region.
    const pinned = !!c.dataSystemManaged || !!c.isPublic;
    const crossCloudBlocked = orgBlocksCloud || pinned;
    const crossRegionBlocked = orgBlocksRegion || pinned;
    if (!crossCloudBlocked && !crossRegionBlocked) return true;
    const cloud = cloudKeyForRegionId(c.cloudRegionId) ?? deriveCatalogLocality(c)?.cloud;
    if (!cloud) return true;
    if (crossCloudBlocked && cloud !== target.cloudKey) return false;
    if (crossRegionBlocked && target.regionId && c.cloudRegionId && c.cloudRegionId !== target.regionId) return false;
    return true;
  });
};
